import {AfterViewInit, Component, ElementRef,Inject, OnDestroy, OnInit, Type, ViewChild} from '@angular/core';
import {NgxFileDropEntry} from 'ngx-file-drop';
import {combineLatest, fromEvent, Subscription} from 'rxjs';
import {NbDialogRef, NbDialogService} from '@nebular/theme';
import {MainPageComponent} from '../../../../../main/components/main/main-page.component';
import {WebServiceCreateComponent} from '../web-service-create/web-service-create.component';
import {ManagementDataBaseComponent} from '../management-data-base/management-data-base.component';
import {debounceTime, distinctUntilChanged, filter, tap} from 'rxjs/operators';
import {DataSource} from '../../../../../data-source/models/data-source.modol';
import {PreviewTableComponent} from '../preview-table/preview-table.component';
import {PreviewExcelComponent} from '../preview-excel/preview-excel.component';
import {PreviewDataSourceInfo} from '../../../../../data-source/models/preview-data-source-info.model';
import {IDataSourcePreview} from '../../interfaces/i-data-source-preview';
import {sortItemList} from './create-dashboard-sortItems';

@Component({
  selector: 'morva-create-dashboard',
  templateUrl: './create-dashboard.component.html',
  styleUrls: ['./create-dashboard.component.scss'],
})
export class CreateDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('dataSourceFilter') inputSearchKey: ElementRef;

  workspaceId: string;
  dashboardId: string;
  addToDashboard: boolean;
  loadingDataSources: boolean;
  uploadingData: boolean;
  inputValue = '';
  files: NgxFileDropEntry[] = [];
  uploadType = 'DataSourceFiles';
  dataSources: Partial<DataSource>[];
  dataSourcesLoading: any;
  selectedDatasourceId: string;
  pageNumber: number;
  totalPages: number;
  pageSize = 4;
  sortItems = sortItemList;
  selectedSort = this.sortItems[0]?.name;
  isDroppedSortTypesDropdown = false;
  observables = [];
  subscriptions: Subscription[] = [];
  noData: boolean;
  creatingDataSource = false;
  isCreatingDataSourceForDashboard = false;
  fileType: string;
  previewDataSourceInfo: PreviewDataSourceInfo;
  isSearching = false;
  waitForResults = false;

  constructor(
    private dialogService: NbDialogService,
    private dialogRef: NbDialogRef<MainPageComponent>,
    @Inject('DataSourcePreview')private dataSourcePreviewFacade: IDataSourcePreview,
  ) {}

  ngOnInit() {
    this.loadingDataSources = true;
    this.dataSourcesLoading = {
      workspaceId: this.workspaceId,
      searchKey: '',
      column: this.sortItems[0]?.id || 'none',
      ascending: false,
      pageNumber: 1,
      pageSize: this.pageSize,
      needCount: true,
    };
    if(this.addToDashboard) {
      this.dataSourcesLoading['dashboardId'] = this.dashboardId;
    }
    this.dataSourcePreviewFacade.updateWorkspaceId(this.workspaceId);
    this.dataSourcePreviewFacade.loadDataSources(this.dataSourcesLoading.workspaceId, this.dataSourcesLoading.searchKey,
        this.dataSourcesLoading.column, this.dataSourcesLoading.ascending, this.dataSourcesLoading.pageNumber,
        this.dataSourcesLoading.pageSize, this.dataSourcesLoading.needCount);
    this.dataSourcePreviewFacade.setAddToDashboard(this.addToDashboard,this.dashboardId);
    this.setSubscribers();
  }

  ngAfterViewInit(): void {
    fromEvent(this.inputSearchKey.nativeElement, 'keyup')
      .pipe(
        filter(Boolean),
        debounceTime(600),
        distinctUntilChanged(),
        tap(() => {
          const searchKey = this.inputSearchKey.nativeElement.value;
          this.searchInDataSources(searchKey);
        }),
      )
      .subscribe();
  }

  setSubscribers(): void {
    this.observables.push(this.dataSourcePreviewFacade.getDataSources$().
    pipe(tap((dataSources: Partial<DataSource>[]) => this.dataSources= dataSources)));
    this.observables.push(this.dataSourcePreviewFacade.getPageNumber$().
    pipe(tap(pageNumber => this.pageNumber = pageNumber)));
    this.observables.push(this.dataSourcePreviewFacade.getTotalPages$().
    pipe(tap(totalPages => this.totalPages = totalPages)));

    this.subscriptions.push(combineLatest(this.observables).subscribe((subscriptions) => {
      this.loadingDataSources = false;
      if (this.waitForResults){
        this.waitForResults = false;
        this.isSearching = false;
      }
      if(this.dataSources.length === 0) {
        this.noData = true;
      } else if (this.dataSources.length > 0) {
        this.noData = false;
      }
    }));

    this.subscriptions.push(this.dataSourcePreviewFacade.getPreviewDataSourceInfo$().subscribe(previewDataSourceInfo => {
      this.previewDataSourceInfo = previewDataSourceInfo;
    }));
    this.subscriptions.push(this.dataSourcePreviewFacade.getSubmitState$().subscribe(
      submit => {
        if(submit === 'submitted' && this.creatingDataSource && !this.addToDashboard) {
          this.dataSourcePreviewFacade.setSubmitState('notSubmitted');
          this.createDataSource();
        } else if(submit === 'submitted' && this.addToDashboard) {
          if (this.selectedDatasourceId) {
            this.dialogRef.close(this.selectedDatasourceId);
          }
          this.dataSourcePreviewFacade.setSubmitState('notSubmitted');
          this.createDataSourceForDashboard();
        } else if (submit === 'submitted') {
          this.uploadingData = false;
          this.dataSourcePreviewFacade.setSubmitState('notSubmitted');
          this.close();
        } else if ( submit === 'notSubmitted') {
          this.uploadingData = false;
        }
      },
    ));
  }

  onFileDrop(droppedFile: File[]): void {
    this.uploadingData = true;
    this.creatingDataSource = true;
    this.dataSourcePreviewFacade.uploadFile(droppedFile[0], this.workspaceId, this.uploadType);
  }

  uploadFileFromURL(url: string): void {
    this.uploadingData = true;
    this.creatingDataSource = true;
    this.dataSourcePreviewFacade.uploadFileFromURL(url, this.workspaceId);
  }

  openWebServiceDialog(): void {
    this.openDialogInstance(WebServiceCreateComponent);
  }

  openDataBaseCreateDialog(type: 'CreateSqlServer' | 'CreateOracle'): void {
    this.openDialogInstance(ManagementDataBaseComponent, type);
  }

  openDialogInstance(component: Type<WebServiceCreateComponent | ManagementDataBaseComponent>,
                     type: 'CreateSqlServer' | 'CreateOracle' = undefined): void {
    const closeOnBackdropClick = false;
    this.creatingDataSource = true;
    this.dialogService.open(component, { context: {
        type,
        workspaceId: this.workspaceId,
      }, closeOnBackdropClick }).onClose.subscribe(
        save => {
          this.dataSourcePreviewFacade.resetConnectionTables();
          if(!save) {
            this.close();
          }
        },
    );
    if(!this.addToDashboard) {
      this.close();
    } else {
      this.isCreatingDataSourceForDashboard = true;
    }
  }

  showSort(): void {
    this.isDroppedSortTypesDropdown = true;
    if (this.selectedSort === this.sortItems[0].name) {
      document.getElementById(this.sortItems[0].id).classList.add('active');
    }
  }

  changeSort(sortItem: any): void {
    this.isDroppedSortTypesDropdown = false;
    this.selectedSort = sortItem.name;
    for (const iterator of this.sortItems) {
      document.getElementById(iterator.id).classList.remove('active');
    }
    document.getElementById(sortItem.id).classList?.add('active');
    this.dataSourcesLoading = {
      workspaceId: this.workspaceId,
      searchKey: '',
      column: sortItem.id,
      ascending: !this.dataSourcesLoading.ascending,
      pageNumber: 1,
      pageSize: this.pageSize,
      needCount: true,
    };
    this.loadingDataSources = true;
    this.dataSourcePreviewFacade.loadDataSources(this.dataSourcesLoading.workspaceId, this.dataSourcesLoading.searchKey,
        this.dataSourcesLoading.column, this.dataSourcesLoading.ascending, this.dataSourcesLoading.pageNumber,
        this.dataSourcesLoading.pageSize, this.dataSourcesLoading.needCount);
  }

  searchInDataSources(searchKey: string): void {
    this.dataSourcesLoading = {
      workspaceId: this.workspaceId,
      searchKey,
      column: 'None',
      ascending: false,
      pageNumber: 1,
      pageSize: 4,
      needCount: true,
    };
    this.isSearching = true;
    if (searchKey.length === 0) {
      this.waitForResults = true;
    }
    this.dataSourcePreviewFacade.loadDataSources(this.dataSourcesLoading.workspaceId, this.dataSourcesLoading.searchKey,
        this.dataSourcesLoading.column, this.dataSourcesLoading.ascending, this.dataSourcesLoading.pageNumber,
        this.dataSourcesLoading.pageSize, this.dataSourcesLoading.needCount);
  }

  setPageNumber(pageNumber: number): void {
    this.dataSourcesLoading.pageNumber = pageNumber;
    this.dataSourcesLoading.needCount = false;
    this.dataSourcePreviewFacade.loadDataSources(this.dataSourcesLoading.workspaceId, this.dataSourcesLoading.searchKey,
        this.dataSourcesLoading.column, this.dataSourcesLoading.ascending, this.dataSourcesLoading.pageNumber,
        this.dataSourcesLoading.pageSize, this.dataSourcesLoading.needCount);
  }

  close(): void {
    this.dialogRef.close();
  }

  makeNewDashboard(dataSource: Partial<DataSource>): void {
    this.uploadingData = true;
    this.creatingDataSource = false;
    const newDashboard = {
      workspaceId: this.workspaceId,
      dataSourceId: dataSource.id,
    };
    this.dataSourcePreviewFacade.makeNewDashboard(newDashboard, this.workspaceId);
  }

  addDataSourceToDashboard(dataSource: Partial<DataSource>): void {
    this.uploadingData = true;
    this.creatingDataSource = false;
    this.selectedDatasourceId = dataSource.id;
    this.dataSourcePreviewFacade.addDataSourceToDashboard(this.workspaceId,dataSource.id,this.dashboardId);
  }

  createDataSource(): void {
    const closeOnBackdropClick = false;
    this.uploadingData = false;
    this.creatingDataSource = false;
    if(!this.previewDataSourceInfo.isMultiSheet) {
      this.dialogService.
      open(PreviewTableComponent, {context: {workspaceId: this.workspaceId, type: 'CreateDataSource'},
        closeOnBackdropClick});
    } else {
      this.dialogService.
      open(PreviewExcelComponent, {context: {workspaceId: this.workspaceId, type: 'CreateDataSource'},
        closeOnBackdropClick});
    }
    this.close();
  }

  createDataSourceForDashboard(): void {
    this.addToDashboard = true;
    if(this.creatingDataSource) {
      const closeOnBackdropClick = false;
      this.creatingDataSource = false;
      this.uploadingData = false;
      this.isCreatingDataSourceForDashboard = true;
      if(!this.previewDataSourceInfo.isMultiSheet) {
        this.dialogService.
        open(PreviewTableComponent, {context: {workspaceId: this.workspaceId, type: 'CreateDataSource'},
          closeOnBackdropClick}).
        onClose.subscribe(
          () => {
            this.dialogRef.close(true);
          });
      } else {
        this.dialogService.
        open(PreviewExcelComponent, {context: {workspaceId: this.workspaceId,  type: 'CreateDataSource'},
          closeOnBackdropClick}).
        onClose.subscribe(
          () => {
            this.dialogRef.close(true);
          });
      }

    } else {
      this.creatingDataSource = false;
      this.dialogRef.close(true);
    }
  }

  onSortDirectionChange(value: boolean) {
    this.dataSourcesLoading = {
      ...this.dataSourcesLoading,
      ascending: value
    }
    this.loadingDataSources = true;
    this.dataSourcePreviewFacade.loadDataSources(this.dataSourcesLoading.workspaceId, this.dataSourcesLoading.searchKey,
        this.dataSourcesLoading.column, this.dataSourcesLoading.ascending, this.dataSourcesLoading.pageNumber,
        this.dataSourcesLoading.pageSize, this.dataSourcesLoading.needCount);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
